import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { localTZ } from "@/lib/push";

const DISMISS = "camryn_tz_keep";
export const cityOf = (tz) => (tz || "").split("/").pop().replace(/_/g, " ");

export async function saveTime(patch, setUser) {
  const { data } = await api.put("/me/time", patch);
  setUser((u) => ({ ...u, tz: data.saved_tz, day_start: data.day_start }));
  return data;
}

// Saves the device time zone the first time, and asks once when the device moves to a new zone.
export function TimeSync() {
  const { user, setUser } = useAuth();
  const device = localTZ();
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (!user || !device) return;
    if (!user.tz) { saveTime({ tz: device }, setUser).catch((e) => console.warn("tz save failed", e?.response?.status)); return; }
    setAsk(user.tz !== device && localStorage.getItem(DISMISS) !== `${user.tz}>${device}`);
  }, [user, device, setUser]);
  if (!ask) return null;
  const keep = () => { localStorage.setItem(DISMISS, `${user.tz}>${device}`); setAsk(false); };
  const swap = async () => {
    try { await saveTime({ tz: device }, setUser); toast.success(`CAMRYN now uses ${cityOf(device)} time.`); setAsk(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fixed inset-x-3 bottom-24 lg:bottom-6 lg:left-auto lg:right-6 lg:w-96 z-50 card p-4 shadow-lg rise" data-testid="tz-prompt">
      <div className="flex gap-3">
        <Globe2 className="h-5 w-5 text-stone-700 shrink-0 mt-0.5" strokeWidth={1.75} />
        <div className="min-w-0">
          <p className="text-sm font-semibold">Looks like you're in {cityOf(device)} time now</p>
          <p className="text-xs text-stone-600 mt-0.5">Switch CAMRYN from {cityOf(user.tz)} so your day and reminders follow you? Today's progress stays where it is.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={swap} data-testid="tz-prompt-switch" className="btn-ink px-4 h-11 rounded-full text-sm font-semibold">Switch</button>
            <button onClick={keep} data-testid="tz-prompt-keep" className="px-4 h-11 rounded-full border border-stone-200 text-sm font-semibold">Keep {cityOf(user.tz)}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const hourLabel = (h) => (h === 0 ? "Midnight" : h === 12 ? "Noon" : h < 12 ? `${h}:00am` : `${h - 12}:00pm`);
const zones = () => { try { return Intl.supportedValuesOf("timeZone"); } catch { return [localTZ()]; } };

export function TimeSettings() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const device = localTZ();
  const save = async (patch, msg) => {
    setBusy(true);
    try { await saveTime(patch, setUser); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const tz = user.tz || device;
  const start = user.day_start ?? 4;
  return (
    <section className="card p-5" data-testid="time-settings">
      <div className="eyebrow mb-1">My day & time zone</div>
      <p className="text-xs text-stone-500 mb-4">Your CAMRYN day doesn't end at midnight. Late nights still count as the same day until your new day begins.</p>
      <label className="block text-sm font-medium mb-1" htmlFor="tz-select">Time zone</label>
      <select id="tz-select" value={tz} disabled={busy} onChange={(e) => save({ tz: e.target.value }, `Time zone set to ${cityOf(e.target.value)}.`)}
        data-testid="time-tz-select" className="w-full h-11 px-3 rounded-xl border border-stone-200 bg-white text-base">
        {zones().map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
      </select>
      {tz !== device && (
        <button onClick={() => save({ tz: device }, `Time zone set to ${cityOf(device)}.`)} disabled={busy} data-testid="time-use-device"
          className="tap mt-2 text-xs underline text-stone-600">Use this device's time zone ({cityOf(device)})</button>
      )}
      <label className="block text-sm font-medium mt-4 mb-1" htmlFor="day-start-select">My new day starts at</label>
      <select id="day-start-select" value={start} disabled={busy} onChange={(e) => save({ day_start: Number(e.target.value) }, `Your new day now starts at ${hourLabel(Number(e.target.value))}.`)}
        data-testid="time-day-start-select" className="w-full h-11 px-3 rounded-xl border border-stone-200 bg-white text-base">
        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}{h === 4 ? " (default)" : ""}</option>)}
      </select>
      <p className="text-[11px] text-stone-500 mt-2">Work nights? Set this to when you usually wake up.</p>
    </section>
  );
}
